import { parsePhoneNumber } from 'libphonenumber-js';

/**
 * Formats a phone number into international display format.
 * Handles E.164, Russian 8-prefix, and already-formatted strings.
 * @example
 * formatPhoneNumber('+79885242737')   // '+7 988 524-27-37'
 * formatPhoneNumber('79885242737')    // '+7 988 524-27-37'
 * formatPhoneNumber('89885242737')    // '+7 988 524-27-37'
 * formatPhoneNumber('+447911123456')  // '+44 7911 123456'
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizeRussianPrefix(phone);

  try {
    const parsed = parsePhoneNumber(normalized);
    if (parsed?.isValid()) {
      return parsed.formatInternational();
    }
  } catch {
    // fall through to raw return
  }

  return phone;
}

/**
 * Converts any phone format to E.164 (e.g. "+79885242737").
 * @example
 * unformatPhoneNumber('+7 (988) 524-27-37') // '+79885242737'
 * unformatPhoneNumber('+44 7911 123456')     // '+447911123456'
 */
export function unformatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';

  const normalized = normalizeRussianPrefix(phone);

  try {
    const parsed = parsePhoneNumber(normalized);
    if (parsed?.isValid()) {
      return parsed.format('E.164');
    }
  } catch {
    // fall through
  }

  // Fallback: strip everything except leading + and digits
  const digits = phone.replaceAll(/\D/g, '');
  if (!digits) return phone;
  return '+' + digits;
}

/**
 * Normalises legacy Russian phone strings:
 * - bare 11-digit strings starting with 8 → replace leading 8 with +7
 * - bare 11-digit strings starting with 7 → prepend +
 * Everything else is returned unchanged.
 */
function normalizeRussianPrefix(phone: string): string {
  const trimmed = phone.trim();

  // Already has a + prefix — nothing to do
  if (trimmed.startsWith('+')) return trimmed;

  const digits = trimmed.replaceAll(/\D/g, '');

  if (digits.length === 11 && digits[0] === '8') {
    return '+7' + digits.slice(1);
  }

  if (digits.length === 11 && digits[0] === '7') {
    return '+' + digits;
  }

  return trimmed;
}
